// src/services/AppwriteService.ts
import { Client, Databases, Account, ID, Storage, OAuthProvider, Models } from "appwrite";

interface ReportDocument extends Models.Document {
  postboxId: string;
  userId: string;
  reason: string;
  description?: string;
  status: 'pending' | 'reviewed' | 'resolved';
  createdAt: string;
}

export class AppwriteService {
  private client: Client;
  private databases: Databases;
  public account: Account;
  private storage: Storage;
  private readonly databaseId: string;
  private readonly collectionId: string;
  private readonly bucketId: string;
  private readonly reportsCollectionId: string;

  constructor() {
    this.client = new Client()
      .setEndpoint('https://appwrite.postbox-explorer.com/v1')
      .setProject('680a28fb000b8057e77d');

    this.databases = new Databases(this.client);
    this.account = new Account(this.client);
    this.storage = new Storage(this.client);
    this.databaseId = "680a6281000082a516f4";
    this.collectionId = "680a62ac000f8d45b152";
    this.bucketId = "680a62d4001469033eec";
    this.reportsCollectionId = "68178514002fa69bf95e"; // You'll need to create this collection and update the ID
  }

  // Auth methods
  async signInWithGoogle() {
    try {
      const successUrl = window.location.origin + '/auth/callback';
      const failureUrl = window.location.origin + '/auth/error';
      
      await this.account.createOAuth2Session(
        OAuthProvider.Google,
        successUrl,
        failureUrl
      );
    } catch (error) {
      console.error('Error during Google sign in:', error);
      throw error;
    }
  }

  async signInWithFacebook() {
    try {
      const successUrl = window.location.origin + '/auth/callback';
      const failureUrl = window.location.origin + '/auth/error';
      
      await this.account.createOAuth2Session(
        OAuthProvider.Facebook,
        successUrl,
        failureUrl
      );
    } catch (error) {
      console.error('Error during Facebook sign in:', error);
      throw error;
    }
  }

  async getCurrentUser() {
    try {
      return await this.account.get();
    } catch (error) {
      return null;
    }
  }

  async signOut() {
    await this.account.deleteSession('current');
  }

  // Database methods
  async createPostbox(data: {
    latitude: number;
    longitude: number;
    royalCipher: string;
    description?: string;
    photoUrl?: string;
  }) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    return this.databases.createDocument(
      this.databaseId,
      this.collectionId,
      "unique()",
      {
        ...data,
        latitude: Math.round(data.latitude * 1000000),
        longitude: Math.round(data.longitude * 1000000),
        createdAt: new Date().toISOString(),
        userId: currentUser.$id
      }
    );
  }

  async listPostboxes() {
    return this.databases.listDocuments(this.databaseId, this.collectionId);
  }

  async deletePostbox(documentId: string) {
    return this.databases.deleteDocument(this.databaseId, this.collectionId, documentId);
  }

  async uploadPhoto(file: File) {
    try {
      // First create the file
      const response = await this.storage.createFile(
        this.bucketId,
        ID.unique(),
        file
      );

      // Then get the file view URL
      const fileView = await this.storage.getFileView(this.bucketId, response.$id);
      
      // Return the URL
      return fileView;
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  }

  async createReport(data: {
    postboxId: string;
    reason: string;
    description?: string;
  }) {
    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    return this.databases.createDocument(
      this.databaseId,
      this.reportsCollectionId,
      "unique()",
      {
        ...data,
        userId: currentUser.$id,
        createdAt: new Date().toISOString(),
        status: 'pending'
      }
    );
  }

  async listReports() {
    console.log('Fetching reports from collection:', this.reportsCollectionId);
    const response = await this.databases.listDocuments<ReportDocument>(this.databaseId, this.reportsCollectionId);
    console.log('Reports response:', response);
    return response;
  }

  async updateReportStatus(reportId: string, status: 'pending' | 'reviewed' | 'resolved') {
    return this.databases.updateDocument<ReportDocument>(
      this.databaseId,
      this.reportsCollectionId,
      reportId,
      { status }
    );
  }

  async deleteReport(reportId: string) {
    return this.databases.deleteDocument(
      this.databaseId,
      this.reportsCollectionId,
      reportId
    );
  }

  async deleteAccount() {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      // Delete all user's postboxes
      const postboxes = await this.listPostboxes();
      const userPostboxes = postboxes.documents.filter(
        (postbox: any) => postbox.userId === currentUser.$id
      );
      
      // Delete each postbox
      for (const postbox of userPostboxes) {
        await this.deletePostbox(postbox.$id);
      }

      // Delete all user's reports
      const reports = await this.listReports();
      const userReports = reports.documents.filter(
        (report) => report.userId === currentUser.$id
      );
      
      // Delete each report
      for (const report of userReports) {
        await this.deleteReport(report.$id);
      }

      // Finally, delete the user account
      await (this.account as any).deleteAccount();
    } catch (error) {
      console.error('Error deleting account:', error);
      throw error;
    }
  }
}

const appwriteService = new AppwriteService();
export default appwriteService;